import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { fmtDateLong, groupByDate } from "../lib/dates";
import { CalendarClock, Clock, Loader2, CalendarX2 } from "lucide-react";

export function RescheduleBookingButton({ slots, onConfirm, testid, current }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState(null);

  const grouped = useMemo(
    () => groupByDate((slots || []).filter((s) => s.status === "available")),
    [slots]
  );

  const confirm = async () => {
    if (!chosen) return;
    setLoading(true);
    try { await onConfirm(chosen); setOpen(false); setChosen(null); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setChosen(null); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-xl border-[#9DC3E6] text-[#2f6ea3] hover:bg-[#9DC3E6]/10 font-bold shrink-0" data-testid={testid}>
          <CalendarClock className="h-4 w-4 mr-1" />Reprogramar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle>Reprogramar cita</DialogTitle>
          <DialogDescription>
            {current ? <>Cita actual: <strong>{current}</strong>. </> : null}
            Elige una nueva franja disponible. La franja anterior quedará libre y se avisará por email.
          </DialogDescription>
        </DialogHeader>

        {grouped.length === 0 ? (
          <div className="flex flex-col items-center text-center py-10 text-[#64748B]">
            <CalendarX2 className="h-8 w-8 mb-2 text-slate-300" />
            <p className="text-sm">No hay franjas libres disponibles ahora mismo.</p>
          </div>
        ) : (
          <div className="max-h-[360px] overflow-y-auto space-y-4 pr-1" data-testid={`${testid}-slots`}>
            {grouped.map(({ date, slots: daySlots }) => (
              <div key={date}>
                <p className="font-bold text-sm text-[#373E45] capitalize mb-2 flex items-center gap-1.5">
                  <CalendarClock className="h-4 w-4 text-[#7FD498]" />{fmtDateLong(date)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {daySlots.map((s) => {
                    const active = chosen === s.id;
                    return (
                      <button key={s.id} type="button" onClick={() => setChosen(s.id)} data-testid={`${testid}-slot-${s.id}`}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border font-bold text-sm transition-colors ${active ? "bg-[#7FD498] border-[#7FD498] text-[#373E45]" : "border-[#7FD498] bg-[#7FD498]/10 text-[#373E45] hover:bg-[#7FD498]/30"}`}>
                        <Clock className="h-4 w-4" />{s.start_time}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)} data-testid={`${testid}-cancel`}>Cancelar</Button>
          <Button onClick={confirm} disabled={!chosen || loading} className="bg-[#373E45] hover:bg-[#2b3137] text-white font-bold rounded-xl" data-testid={`${testid}-confirm`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover cita aquí"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
